<template>
  <svg
    :viewBox="viewBox"
    :width="width"
    :height="height"
    :fill="computedColor"
    :stroke="stroke"
  ></svg>
</template>

<script lang="ts">
import Vue from 'vue'
import { parse } from 'postsvg'
import render from 'posthtml-render'
import colors from 'vuetify/es5/util/colors'

// Кэш загруженных иконок
const cache: Map<string, Promise<string>> = new Map()

// Типы
interface ParsedSVGNode {
  attrs: Record<string, string>
  content?: any[]
  [key: string]: any
}

interface ParsedSVG {
  root: {
    attrs: Record<string, string>
    content: any[]
  }
  each: (selector: string, callback: (node: ParsedSVGNode) => void) => void
  [key: string]: any
}

interface ComponentData {
  svgString: string
  colors: Record<string, string>
}

export default Vue.extend({
  name: 'SVGIcon',

  props: {
    // Имя файла (без расширения)
    name: {
      type: String,
      required: true
    },
    // Ширина
    width: {
      type: [Number, String] as Vue.PropType<number | string>,
      default: '100%'
    },
    // Высота
    height: {
      type: [Number, String] as Vue.PropType<number | string>,
      default: '100%'
    },
    // Цвет заливки - можно указывать цвет из темы vuetify, например primary
    color: {
      type: String,
      default: 'grey'
    },
    // Цвет обводки
    stroke: {
      type: String,
      default: 'none'
    },
    // Генерировать ли событие ready иконки
    generateReadyEvent: {
      type: Boolean,
      default: false
    }
  },

  data(): ComponentData {
    return {
      // Строка с содержимым SVG-файла
      svgString: '',
      colors: {}
    }
  },

  computed: {
    // Путь расположения SVG-файла
    filepath(): string {
      try {
        return require(`./files/${this.name}.svg`)
      } catch (e) {
        return require(`./files/file_not_found.svg`)
      }
    },

    // Распарсенное дерево SVG-файла
    parsedSVG(): ParsedSVG | null {
      return this.svgString ? parse(this.svgString) as ParsedSVG : null
    },

    // Размер холста
    viewBox(): string {
      return this.parsedSVG ? this.parsedSVG.root.attrs.viewBox : '0 0 20 20'
    },

    computedColor(): string {
      let $_color_palette: string[] = Object.keys(this.$vuetify.theme)
      $_color_palette = $_color_palette.concat(Object.keys(colors))
      $_color_palette.forEach(item => {
        if (item !== 'shades') {
          let $_color: string | undefined = this.$vuetify.theme[item] || (colors[item] && (colors[item] as any).base)
          this.$set(this.colors, item, $_color)
        }
      })
      return this.colors[this.color] || this.color
    },
  },

  watch: {
    // При изменении файла, перезагружаем его
    filepath: {
      immediate: true,
      handler: 'loadFile'
    },
    // При загрузке другого файла обновляем SVG
    svgString: 'refreshSvg',
    // При изменениях стилизации обновляем SVG
    stroke: 'refreshSvg',
    color: 'refreshSvg'
  },

  methods: {
    // Загрузка содержимого SVG файла
    loadFile(): void {
      this.getSvgIconText()
        .then((responseText: string) => (this.svgString = responseText))
        .catch((error: Error) => {
          // console.error('Ошибка загрузки SVG-файла', error)
          this.getSvgIconText('file_not_found')
          // file_not_found
        })
    },

    // Получение SVG-файла в виде текстовой строки
    // + кэширование ранее загруженных файлов
    async getSvgIconText(fallbackName?: string): Promise<string> {
      let url: string
      const iconName = fallbackName || this.name
      
      try {
        url = require(`./files/${iconName}.svg`)
      } catch (e) {
        console.log(iconName, 'file_not_found', e)
        url = require(`./files/file_not_found.svg`)
      }
      
      // console.log('getSvgIconText', this.name)
      // Кэшируем загружаемые иконки
      // для сокращения запросов к серверу
      if (!cache.has(url)) {
        try {
          cache.set(url, fetch(url).then(r => r.text()))
        } catch (e) {
          // console.error('Ошибка getSvgIconText', e)
          cache.delete(url)
        }
      }

      // Возвращаем содержимое из кэша
      if (cache.has(url)) {
        return await cache.get(url)!
      }
      return Promise.reject(new Error('Нет SVG-файла в локальном кэше'))
    },

    // Обновление SVG
    refreshSvg(): void {
      Promise.resolve(this.parsedSVG)
        // Обновляем стилизацию
        .then((svgTree: ParsedSVG | null) => {
          if (!svgTree) return svgTree
          svgTree.each('path', (node: ParsedSVGNode) => (node.attrs.fill = this.color))
          // svgTree.each('circle', node => (node.attrs.fill = this.color))
          //svgTree.each('rect', node => (node.attrs.stroke = this.color))
          //svgTree.each('ellipse', node => (node.attrs.stroke = this.color))
          //svgTree.each('line', node => (node.attrs.stroke = this.color))
          //svgTree.each('polyline', node => (node.attrs.stroke = this.color))
          return svgTree
        })
        // Оставляем только внутренности тега <svg>
        .then((svgTree: ParsedSVG | null) => {
          if (!svgTree) return ''
          return render(svgTree.root.content)
        })
        // Заменяем содержимое тега <svg> новым
        .then((svgHtml: string) => {
          if (this.$el) {
            this.$el.innerHTML = svgHtml
          }
        })
        .then(() => {
          if (this.generateReadyEvent) {
            this.$emit('ready')
          }
        })
        .catch((error: Error) => {
          console.log('Ошибка при обновлении SVG', error)
          this.$emit('error', error)
        })
    }
  }
})
</script>

<style>

</style>